import React, { useMemo, useRef } from 'react'
import clx from 'classnames'
import { connect } from 'react-redux'
import { FormSection, formValueSelector } from 'redux-form'
import _ from 'lodash'
import { Icon, Button } from '@vzmi/shp_shared_compo'
import SortableTable, {
  DragHandler
} from '../../../../Common/SortableTable/SortableTable'
import NoData from '../../../../Common/NoData/NoData'
import { UPDATE_LISTING_FORM_KEY } from '../../../../../constants/proposalTypes'
import { RENDER_ID } from '../../../UpdateListing/helpers/converters'
import {
  COLUMN,
  FORM_SECTION,
  TABLE_GROUP_TYPE,
  UPDATE_COLLECT_KEY,
  GROUP_SKU_KEY,
  getGroupFirstIndex
} from './helper'
import listingModelStyles from '../index.css'
import styles from './index.css'
import { openPortal } from './withTableActions'

const locale = {
  emptyText: <NoData />
}

// 選擇 spec 後會觸發重新分群，從這裡判斷資料列被重新分群時加上 highlight 效果
const getRowClassName = (record) => {
  const classNames = [styles.tableRow]
  if (record[UPDATE_COLLECT_KEY]) {
    classNames.push(styles.isUpdatedCollection)
  }
  return clx(...classNames)
}

const ExtraModelTable = props => {
  const {
    change,
    readOnly: pageReadOnly,
    tableType,
    tableData,
    extraModelGroups,
    columns = [],
    isMultipleAttrType,
    removeRow
  } = props
  const tableRef = useRef(null)

  const updateCollectKey = useMemo(() => {
    // 排序變更時整個 table 才會 rerender
    const tableRenderId = _.flow(
      () => _.reduce(tableData, (keys, item) => {
        const newKeys = _.map([RENDER_ID], field => {
          return _.get(item, field)
        })
        return [...keys, ...newKeys]
      }, []),
      keys => _.join(keys)
    )()
    return `${tableRenderId}${pageReadOnly}`
  }, [tableData, pageReadOnly])

  const extraModelGroupMapByGroupName = useMemo(() => {
    return _.keyBy(extraModelGroups, COLUMN.GROUP_NAME)
  }, [extraModelGroups])

  // 表格預設第一欄拖曳按鈕、最後一欄刪除按鈕
  const columnsWithActions = [
    {
      key: 'sortableIcon',
      width: 50,
      align: 'center',
      render: (text, record) => {
        const { index, readOnly, isNoAttr } = record
        // 此列為無屬性且同時存在有屬性時隱藏拖曳
        if (isNoAttr && isMultipleAttrType) {
          return null
        }

        return (
          <DragHandler
            id={index}
            // 頁面唯獨或單列唯獨不允許拖曳
            disabled={pageReadOnly || readOnly}
          />
        )
      }
    },
    ...columns,
    {
      key: 'action',
      width: 110,
      align: 'right',
      render: (text, record) => {
        if (pageReadOnly) {
          return null
        }

        return (
          <Button
            buttonType='outline'
            size='small'
            className={clx(
              listingModelStyles.actionBtn,
              listingModelStyles.deleteBtn,
              listingModelStyles.visible
            )}
            onClick={() => removeRow(record)}
            disabled={pageReadOnly}
          >
            <Icon
              name='delete'
              color='#979ba7'
              align='middle'
              size={16}
            />
          </Button>
        )
      }
    }
  ]

  const checkToStoreAction = (sortTableProps = {}) => {
    const { oldIndex, newIndex, collection } = sortTableProps
    const groupSku = _.get(extraModelGroupMapByGroupName, `${collection}.${GROUP_SKU_KEY}`)
    const isNoAttrRowChange = _.isUndefined(groupSku)
    // 無屬性排序變更不提示
    if (isNoAttrRowChange) {
      return false
    }

    // 有屬性順序變更且影響第一筆
    const groupFirstIndex = getGroupFirstIndex(tableData, groupSku)
    const isChangeFirstRow = (
      (oldIndex !== newIndex) &&
      _.includes([oldIndex, newIndex], groupFirstIndex)
    )
    if (!isChangeFirstRow) {
      return false
    }

    const {
      executeConfirmAction,
      executeCancelAction
    } = tableRef.current || {}
    openPortal({
      content: '更換排序後，贈品代表圖將被替換，你確定要更換商品排序？',
      // 所以要綁 reference 才能存取 sortable table component `this`
      onOk: executeConfirmAction.bind(tableRef.current),
      onCancel: executeCancelAction.bind(tableRef.current)
    })
    return true
  }

  const onSortEnd = newTableData => {
    change(`${FORM_SECTION}.${tableType}`, newTableData)
  }

  return (
    <FormSection name={FORM_SECTION}>
      <SortableTable
        key={updateCollectKey}
        ref={ref => (tableRef.current = ref)}
        data={tableData}
        locale={locale}
        columns={columnsWithActions}
        className={styles.table}
        rowClassName={getRowClassName}
        checkToStoreAction={checkToStoreAction}
        onSortEnd={onSortEnd}
      />
    </FormSection>
  )
}

const selector = formValueSelector(UPDATE_LISTING_FORM_KEY)
const ExtraModelTableWithRedux = connect((state, props) => {
  const { tableType } = props
  const {
    [TABLE_GROUP_TYPE[tableType]]: extraModelGroups = {}
  } = selector(state, FORM_SECTION) || {}

  const reduxProps = {
    extraModelGroups
  }
  return reduxProps
})(ExtraModelTable)

export default ExtraModelTableWithRedux
